import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, NavLink, Link } from 'react-router-dom'
import { useWorkspaceLink } from '@/hooks/useWorkspaceSelection'
import {
  Home,
  LayoutDashboard,
  Users,
  TrendingUp,
  Kanban,
  Contact,
  Building2,
  Layers,
  Activity,
  CheckSquare,
  Phone,
  Calendar,
  Mail,
  BarChart3,
  CalendarDays,
  GitBranch,
  UserCog,
  Shield,
  Users2,
  Settings,
  Bell,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  Zap,
  MailOpen,
  FileText,
  ClipboardList,
  type LucideIcon,
} from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { cn } from '@/utils/cn'

// Local NavSection type using LucideIcon
interface LocalNavItem {
  key: string
  label: string
  path: string
  icon: LucideIcon
  badge?: number
}

interface LocalNavSection {
  key: string
  label: string
  items: LocalNavItem[]
}

const NAV_SECTIONS: LocalNavSection[] = [
  {
    key: 'main',
    label: '',
    items: [
      {
        key: 'home',
        label: 'Home',
        path: '/home',
        icon: Home,
      },
      {
        key: 'dashboard',
        label: 'Dashboard',
        path: '/dashboard',
        icon: LayoutDashboard,
      },
      // Top level, next to Dashboard, rather than inside Contacts. A workspace
      // is not a kind of contact record — it is the lens every other page is
      // filtered through, so it belongs beside the other whole-app views.
      {
        key: 'workspaces',
        label: 'Workspaces',
        path: '/workspaces',
        icon: Layers,
      },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    items: [
      { key: 'leads', label: 'Leads', path: '/leads', icon: Users },
      { key: 'deals', label: 'Deals', path: '/deals', icon: TrendingUp },
      { key: 'pipeline', label: 'Pipeline', path: '/pipeline', icon: Kanban },
    ],
  },
  {
    key: 'contacts',
    label: 'Contacts',
    items: [
      { key: 'contacts', label: 'Contacts', path: '/contacts', icon: Contact },
      { key: 'organizations', label: 'Organizations', path: '/crm-organizations', icon: Building2 },
    ],
  },
  {
    key: 'activities',
    label: 'Activities',
    items: [
      { key: 'activities', label: 'Activities', path: '/activities', icon: Activity },
      { key: 'tasks', label: 'Tasks', path: '/tasks', icon: CheckSquare },
      { key: 'notes', label: 'Notes', path: '/notes', icon: FileText },
      { key: 'calls', label: 'Calls', path: '/calls', icon: Phone },
      { key: 'meetings', label: 'Meetings', path: '/meetings', icon: Calendar },
      { key: 'emails', label: 'Emails', path: '/emails', icon: Mail },
      { key: 'email-accounts', label: 'Email Accounts', path: '/email-accounts', icon: MailOpen },
      { key: 'email-templates', label: 'Email Templates', path: '/email-templates', icon: FileText },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    items: [
      { key: 'reports', label: 'Reports', path: '/reports', icon: BarChart3 },
      { key: 'calendar', label: 'Calendar', path: '/calendar', icon: CalendarDays },
      { key: 'notifications', label: 'Notifications', path: '/notifications', icon: Bell },
    ],
  },
  {
    key: 'automation',
    label: 'Automation',
    items: [
      { key: 'workflows', label: 'Workflows', path: '/workflows', icon: GitBranch },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    items: [
      { key: 'admin-users', label: 'Users', path: '/admin/users', icon: UserCog },
      { key: 'admin-roles', label: 'Roles & Permissions', path: '/admin/roles', icon: Shield },
      { key: 'admin-teams', label: 'Teams', path: '/admin/teams', icon: Users2 },
      { key: 'admin-assignment', label: 'Assignment Rules', path: '/admin/assignment-rules', icon: GitBranch },
      { key: 'admin-settings', label: 'Settings', path: '/admin/settings', icon: Settings },
      { key: 'admin-automation', label: 'Automation', path: '/admin/automation', icon: Zap },
      { key: 'admin-workflows', label: 'Pipeline Workflows', path: '/admin/workflows', icon: GitBranch },
      { key: 'admin-audit-logs', label: 'Audit Logs', path: '/admin/audit-logs', icon: Shield },
    ],
  },
]

function isAdminUser(user: { role?: { name?: string } } | null): boolean {
  if (!user?.role?.name) return false
  const name = user.role.name.toLowerCase()
  // org_admin is the role assigned to an organization's administrator (the org
  // creator), so it must see the admin section too.
  return name === 'admin' || name === 'super_admin' || name === 'superadmin' || name === 'org_admin'
}

export function Sidebar() {
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const { sidebarCollapsed, toggleSection, loadSidebarState, isSidebarMini, toggleSidebarMini } = useUIStore()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  useEffect(() => {
    loadSidebarState()
  }, [loadSidebarState])

  // Determine which sections contain the active route
  const activeSectionKey = NAV_SECTIONS.find((section) =>
    section.items.some((item) => location.pathname.startsWith(item.path))
  )?.key

  // Auto-expand section containing active route on initial load
  useEffect(() => {
    if (activeSectionKey && sidebarCollapsed[activeSectionKey]) {
      // Don't auto-collapse active section
    }
  }, [activeSectionKey, sidebarCollapsed])

  const isAdmin = isAdminUser(user)

  function handleLogout() {
    setShowLogoutConfirm(true)
  }

  function confirmLogout() {
    setShowLogoutConfirm(false)
    logout()
  }

  return (
    <>
    <aside className={cn(
      // `hidden lg:flex` as utilities (not the .sidebar-desktop component class) so
      // the `flex` utility can't override the hide — keeps the rail off mobile,
      // where the bottom nav + More drawer handle navigation.
      "fixed left-0 top-0 h-full border-r hidden lg:flex flex-col z-30 theme-sidebar transition-[width] duration-200 ease-in-out overflow-hidden",
      isSidebarMini ? "w-[64px]" : "w-[220px]"
    )}>
      {/* Logo Area */}
      <div className="flex-shrink-0 border-b border-white/30">
        {isSidebarMini ? (
          <div className="flex flex-col items-center py-3 gap-2">
            <img
              src="/artech-icon-v2.png"
              alt="Artech"
              draggable={false}
              style={{ width: 32, height: 32, objectFit: 'contain', display: 'block', flexShrink: 0 }}
            />
            <button
              onClick={toggleSidebarMini}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-white/20 transition-colors"
              title="Expand sidebar"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        ) : (
          <div className="px-4 py-3.5 flex items-center gap-2.5">
            <img
              src="/artech-icon-v2.png"
              alt="Artech"
              draggable={false}
              style={{ width: 38, height: 38, objectFit: 'contain', display: 'block', flexShrink: 0 }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight text-gray-800 dark:text-slate-100">
                AR SalesMine
              </p>
              <p className="text-[11px] text-gray-400 dark:text-slate-400 leading-none mt-0.5">
                CRM
              </p>
            </div>
            <button
              onClick={toggleSidebarMini}
              className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-white/20 transition-colors"
              title="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        {NAV_SECTIONS.map((section) => {
          if ((section.key === 'admin' || section.key === 'automation') && !isAdmin) return null

          // Mini mode — flat list of icons, no section labels
          if (isSidebarMini) {
            return (
              <div key={section.key} className={section.label ? "mt-2" : ""}>
                {section.label && (
                  <div className="mx-auto w-6 border-t border-gray-200/50 dark:border-slate-700/50 mb-1" />
                )}
                {section.items.map((item) => (
                  <SidebarItem key={item.key} item={item} location={location} mini />
                ))}
              </div>
            )
          }

          const isCollapsed = sidebarCollapsed[section.key] ?? false

          if (section.label) {
            return (
              <div key={section.key} className="mb-1">
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center justify-between px-4 py-1.5 group"
                >
                  <span className="text-[10px] font-bold sidebar-section-label uppercase tracking-widest">
                    {section.label}
                  </span>
                  {isCollapsed ? (
                    <ChevronRight size={12} className="sidebar-section-label group-hover:opacity-80 transition-opacity" />
                  ) : (
                    <ChevronDown size={12} className="sidebar-section-label group-hover:opacity-80 transition-opacity" />
                  )}
                </button>
                {!isCollapsed && (
                  <div>
                    {section.items.map((item) => (
                      <SidebarItem key={item.key} item={item} location={location} />
                    ))}
                  </div>
                )}
              </div>
            )
          }

          return (
            <div key={section.key} className="mb-1">
              {section.items.map((item) => (
                <SidebarItem key={item.key} item={item} location={location} />
              ))}
            </div>
          )
        })}
      </nav>

      {/* User footer */}
      <div className="flex-shrink-0 border-t border-mist dark:border-slate-700 p-3">
        {isSidebarMini ? (
          <div className="flex flex-col items-center gap-2">
            <Link to="/profile" title={user?.fullName || 'Profile'} className="rounded-full focus:outline-none hover:ring-2 hover:ring-accent hover:ring-offset-1 transition-all">
              <Avatar name={user?.fullName || user?.firstName} src={user?.avatar} size="sm" />
            </Link>
            <button
              onClick={handleLogout}
              className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Sign out"
            >
              <LogOut size={13} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 min-w-0 sidebar-user-btn px-2 py-1.5">
            <Link to="/profile" className="flex-shrink-0 rounded-full focus:outline-none hover:ring-2 hover:ring-accent hover:ring-offset-1 transition-all" title="View profile">
              <Avatar name={user?.fullName || user?.firstName} src={user?.avatar} size="sm" />
            </Link>
            <Link to="/profile" className="flex-1 min-w-0 hover:opacity-80 transition-opacity">
              <p className="text-xs font-semibold text-navy dark:text-slate-100 truncate leading-tight">
                {user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
              </p>
              <p className="text-[10px] text-steel dark:text-slate-400 truncate leading-tight">
                {user?.role?.name || 'Member'}
              </p>
            </Link>
            <button
              onClick={handleLogout}
              className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>

    </aside>

      {/* Logout Confirmation — rendered at document.body via portal so it escapes sidebar's stacking context */}
      {showLogoutConfirm && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowLogoutConfirm(false)}
          />
          {/* Modal */}
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-7 w-[320px] mx-4">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <LogOut className="w-6 h-6 text-red-500" />
              </div>
            </div>
            {/* Text */}
            <h3 className="text-base font-bold text-gray-900 dark:text-white text-center mb-2">
              Sign Out
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center mb-6">
              Are you sure you want to sign out? You'll need to log in again to access the CRM.
            </p>
            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-colors"
              >
                Yes, Sign Out
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

interface SidebarItemProps {
  item: LocalNavItem
  location: ReturnType<typeof useLocation>
  mini?: boolean
}

function SidebarItem({ item, location, mini }: SidebarItemProps) {
  // Carry the workspace across the click. Without this the sidebar is the one
  // place in the app that silently resets the user's context.
  const withWorkspace = useWorkspaceLink()
  const isActive =
    item.path === '/home'
      ? location.pathname === '/home' || location.pathname === '/'
      : item.path === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname.startsWith(item.path)

  const Icon = item.icon

  if (mini) {
    return (
      <NavLink
        to={withWorkspace(item.path)}
        title={item.label}
        className={cn(
          'sidebar-nav-item flex items-center justify-center w-10 h-10 mx-auto rounded-md transition-colors border-r-2 border-transparent',
          isActive ? 'nav-active font-semibold' : 'font-medium'
        )}
      >
        <Icon size={18} className="flex-shrink-0" />
      </NavLink>
    )
  }

  return (
    <NavLink
      to={withWorkspace(item.path)}
      className={cn(
        'sidebar-nav-item flex items-center gap-2.5 mx-2 px-3 py-2 rounded-md text-sm transition-colors border-r-2 border-transparent',
        isActive ? 'nav-active font-semibold' : 'font-medium'
      )}
    >
      <Icon size={16} className="flex-shrink-0" />
      <span className="truncate">{item.label}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <span
          className="ml-auto text-[10px] font-bold text-white rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--accent)' }}
        >
          {item.badge > 9 ? '9+' : item.badge}
        </span>
      )}
    </NavLink>
  )
}

export default Sidebar
